import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { size as _size } from 'lodash';
import { Stage, Layer, Rect, Circle, Line, Group } from 'react-konva';

import { alphabets as _alphabets } from './WordSearchAlphabets';
import generateWordSearch from '../../ts/games/wordsearch.ts';
import Konva from 'konva';

const prepareWordSearch = (array, words) => {
	const grid = array.map((row, row_index) => {
		return row.map((letter, col) => {
			let is_ok = false;
			words.map((w) => {
				if(w.ok_letters[col] != undefined && w.ok_letters[col][row_index] != undefined) is_ok = true;
			});

			return {
				letter,
				is_ok
			};
		})
	});

	return grid;
};

const WordSearchCanvas = forwardRef(({ stage, ...props }, ref) => {
	const is_builder = props?.is_builder ?? false;
	const is_test = props?.is_test ?? false;
	const is_debug = props?.is_debug ?? false;	// set to true to visually see the words to find within the game board

	const getMinColumns = () => {
		if(stage.wordsearch_words == undefined) return 5;
		
		let min_size_words = props?.columns ?? 0;
		stage.wordsearch_words.map((word) => {
			min_size_words = Math.max(min_size_words, word.length);
		});

		return min_size_words;
	};

	const [ full_loaded, setFullLoaded ] = useState(false);
	const [ force, setForce ] = useState(1);

	const level = stage?.wordsearch_level ?? 'easy';
	const bg_color = stage?.bg_color ?? 'rgba(255, 255, 255, 1)';
	const text_color = stage?.text_color ?? 'rgba(0, 0, 0, 1)';
	const text_color_ok = stage?.highlight_text_color ?? 'rgba(255, 255, 255, 1)';
	const color_ok = stage?.highlight_color ?? 'rgba(120, 195, 0, 1)';
	const color_ko = stage?.missed_color ?? 'rgba(215, 35, 60, 1)';
	const selection_color = stage?.wordsearch_selection_color ?? 'rgba(0, 0, 0, 0.3)';
	const charset_preset = stage?.wordsearch_charset_preset ?? 'latin';
	const custom_charset = stage?.wordsearch_custom_charset ?? '';
	const found_stroke_width = stage?.wordsearch_word_border_width ?? 2;
	const found_stroke_color = stage?.wordsearch_word_border_color ?? text_color;
	const display_outter_border = stage?.display_outter_border ?? true;
	const delay_to_success = 0.5; // in seconds

	const words = useRef((stage?.wordsearch_words ?? []).sort());
	const size = useRef(600);
	const refs = useRef([]);
	//const allOrientations = useRef([]);
	const move_direction = useRef(false);
	const columns = useRef(getMinColumns());
	const start_line = useRef({ x:false, y:false });
	const end_line = useRef({ x:false, y:false });
	const puzzle = useRef([]);
	const score = useRef(0);
	const is_playable = useRef(false);
	const words_in_game = useRef([]);
	const box_size = useRef(10);
	const is_mounting = useRef(false);

	const preventLeaving = (e) => {
		e.returnValue = "There are unsaved changes. Leave now?";
	};

	useEffect(() => {
		//setDifficultLevel();
		resetGame(true);
		gameInit();

		if(!is_builder) window.addEventListener('beforeunload', preventLeaving);

		return () => {
			if(props.endSoundtrack != undefined) props.endSoundtrack(true);

			if(!is_builder) window.removeEventListener('beforeunload', preventLeaving);
		};
	}, []);

	useEffect(() => {
		let changes_puzzle = 0;

		if(JSON.stringify(stage.wordsearch_words) != JSON.stringify(words.current)) {
			changes_puzzle++;
			words.current = stage.wordsearch_words;
		}

		if(stage.wordsearch_size != columns.current) {
			columns.current = stage.wordsearch_size; changes_puzzle++;
		}

		resetGame();

		if(changes_puzzle > 0) {
			//setDifficultLevel();
			gameInit();
		}
	}, [
		stage.wordsearch_size,
		stage.wordsearch_words,
		stage.wordsearch_charset_preset,
		stage.wordsearch_custom_charset,
		stage.wordsearch_level,
	]);

	useEffect(() => {
		if(!is_builder || is_test) return;
		updateGameColors();
	}, [
		stage.bg_color,
		stage.text_color,
		stage.highlight_color,
		stage.highlight_text_color,
		stage.wordsearch_selection_color,
		stage.wordsearch_word_border_width,
		stage.wordsearch_word_border_color,
	]);

	const default_cache_props = { pixelRatio:2, imageSmoothingEnabled:true, offset:5, drawBorder:false };

	/*START SOUNDS*/
	const hover_sound = props.sound_fx?.selecting;

	const hoverStop = (cb) => {
		hover_sound.fade(1, 0, 200);
		hover_sound.once('fade', () => {
			hover_sound.stop();
			hover_sound.volume(1);
			(cb !== undefined && cb == 'success') ? foundPlay() : errorPlay();
		});
	};

	const hoverPlay = () => hover_sound.play();
	const errorPlay = () => props?.sound_fx?.error.play();
	const foundPlay = () => props?.sound_fx?.found.play();
	const successPlay = () => props?.sound_fx?.success.play();
	const failPlay = () => props?.sound_fx?.fail.play();
	/*END SOUNDS*/

	useImperativeHandle(ref, () => ({
		stopGame(success_game, cb) {
			is_playable.current = false;

			(success_game) ? successPlay() : failPlay();

			if(!is_builder) window.removeEventListener('beforeunload', preventLeaving);

			unmountBoardAnimation(success_game, () => {
				setTimeout(() => {
					cb(success_game);
				}, (delay_to_success * 1000));
			});
		},
	}), []);

	const resetGame = (onInit = false) => {
		is_mounting.current = false;
		if(onInit === true) return;

		setFullLoaded(false);
		gameInit();
	};

	const gameInit = () => {
		const new_word_search = generateWordSearch({
			columns:parseInt(stage.wordsearch_size),
			level,
			words:words.current,
			characterSet:getCharactersInGame(),
			fillBlanks:!is_debug,
		});

		puzzle.current = prepareWordSearch(new_word_search.get2D(),new_word_search.words);
		words_in_game.current = new_word_search.getWords();

		if(is_debug) {
			console.table(new_word_search.get2D());
		}

		applyLettersCache();

		box_size.current = Math.abs(size.current / (puzzle?.current ?? []).length);

		clearLettersCache();

		if(stage.wordsearch_words.length > 0 && props.adaptToContainer != undefined) {
			props.adaptToContainer((_size) => {
				size.current = _size;
				setFullLoaded(true);

				setTimeout(() => {
					if(refs.current['layer'] == undefined) return;

					prepareToMount();

					if(is_playable.current === false) {
						mountBoardAnimation();
					} else {
						applyLettersCache();
					}
				}, 50);
			});
		} else {
			setFullLoaded(true);
			applyLettersCache();
		}
	};

	const convertCustomCharsToArray = () => {
		return(custom_charset != "") ? custom_charset.split(",") : [];
	};

	const getCharactersInGame = () => {
		const default_alphabet = ['a','b','c','d','e','f','g','h','i','j','k','l','m','n','o','p','q','r','s','t','u','v','w','x','y','z'];
		const alphabet = _alphabets.find((o) => (o.id == charset_preset));

		let charset = (alphabet) ? alphabet.charset : default_alphabet;
		if(custom_charset.length > 0) {
			charset = charset.concat(convertCustomCharsToArray());
		}

		return charset;
	};

	const checkSolved = () => {
		let is_solved = true;
		words_in_game.current.map((item) => {
			if((item?.found ?? false) === false) is_solved = false
		});
		return is_solved;
	}

	const isSolved = () => {
		let is_solved = checkSolved();
		
		if(is_solved) {
			props.onEnded(is_solved);
		}

		return is_solved;
	};

	//Functions text
	const handleMouseOver = (e) => {
		if(!is_playable.current) return;

		const attrs = e.target.attrs;

		if(start_line.current.x !== false) {
			if(start_line.current.x == end_line.current.x && start_line.current.y == end_line.current.y) {
				move_direction.current = false;
			}

			if(move_direction.current === false) {
				end_line.current = { x:attrs.pos_x, y:attrs.pos_y };

				getMoveDirection();
			} else {
				const displace = Math.min(Math.abs(attrs.pos_x - start_line.current.x), Math.abs(attrs.pos_y - start_line.current.y));

				switch(move_direction.current) {
					case 'diagonal':
						end_line.current = { x:start_line.current.x + displace, y:start_line.current.y + displace };
						break;

					case 'diagonalBack':
						end_line.current = { x:start_line.current.x - displace, y:start_line.current.y + displace };
						break;

					case 'diagonalUp':
						end_line.current = { x:start_line.current.x + displace, y:start_line.current.y - displace };
						break;

					case 'diagonalUpBack':
						end_line.current = { x:start_line.current.x - displace, y:start_line.current.y - displace };
						break;

					case 'horizontal':
					case 'horizontalBack':
						end_line.current = { x:attrs.pos_x, y:start_line.current.y };
						break;

					case 'vertical':
					case 'verticalUp':
						end_line.current = { x:start_line.current.x, y:attrs.pos_y };
						break;
				}
			}

			setForce(-1 * force);
		}

		setTimeout(() => {
			// automatic mouseUp:
			if(autoEndLine()) handleMouseUp(false);
		}, 1000);
	};

	const autoEndLine = () => {
		const first_p = 0;
		const last_p = puzzle.current.length - 1;

		if(move_direction.current == 'diagonal' && (end_line.current.x == last_p || end_line.current.y == last_p)) return true;
		if(move_direction.current == 'diagonalBack' && (end_line.current.x == first_p || end_line.current.y == last_p)) return true;
		if(move_direction.current == 'diagonalUp' && (end_line.current.x == last_p || end_line.current.y == first_p)) return true;
		if(move_direction.current == 'diagonalUpBack' && (end_line.current.x == first_p || end_line.current.y == first_p)) return true;
		if(move_direction.current == 'horizontal' && (end_line.current.x == last_p)) return true;
		if(move_direction.current == 'horizontalBack' && (end_line.current.x == first_p)) return true;
		if(move_direction.current == 'vertical' && (end_line.current.y == last_p)) return true;
		if(move_direction.current == 'verticalUp' && (end_line.current.y == first_p)) return true;
		return false;
	};

	const getMoveDirection = () => {
		const ini = start_line.current;
		const end = end_line.current;

		const x_diff = Math.abs(end.x - ini.x);
		const y_diff = Math.abs(end.y - ini.y);

		let angle = Math.atan(y_diff / x_diff) * 180 / Math.PI;
		if(isNaN(angle)) angle = 0;

		if(angle == 45) {
			if(ini.x < end.x && ini.y > end.y) {
				move_direction.current = 'diagonalUp';
			} else if(ini.x > end.x && ini.y > end.y) {
				move_direction.current = 'diagonalUpBack';
			} else if(ini.x < end.x && ini.y < end.y) {
				move_direction.current = 'diagonal';
			} else {
				move_direction.current = 'diagonalBack';
			}
		} else if(angle < 45) {
			move_direction.current = (ini.x < end.x) ? 'horizontal' : 'horizontalBack';
			end_line.current.y = start_line.current.y;
		} else {
			move_direction.current = (ini.y < end.y) ? 'vertical' : 'verticalUp';
			end_line.current.x = start_line.current.x;
		}
	};

	const handleMouseDown = (e) => {
		if(!is_playable.current) return;

		hoverPlay();

		const attrs = e.target.attrs;

		start_line.current = { x:attrs.pos_x, y:attrs.pos_y };
		end_line.current = { x:attrs.pos_x, y:attrs.pos_y };
		setForce(-1 * force);
	};

	const handleMouseUp = (e) => {
		if(!is_playable.current) return;

		checkWord();

		move_direction.current = false;
		start_line.current = { x:false, y:false };
		end_line.current = { x:false, y:false };
		setForce(-1 * force);
	};

	const checkWord = () => {
		if(end_line.current.x === false) return;

		let first_letter = start_line.current;
		let last_letter = end_line.current;
		let found_index = -1;

		words_in_game.current.map((word, w) => {
			const check_word_in_logical_direction = word.start[0] == first_letter.x && word.start[1] == first_letter.y && word.end[0] == last_letter.x && word.end[1] == last_letter.y;

			const check_word_in_reverse_direction = word.start[0] == last_letter.x && word.start[1] == last_letter.y && word.end[0] == first_letter.x && word.end[1] == first_letter.y;

			if((word?.found ?? false) === false && (check_word_in_logical_direction || check_word_in_reverse_direction)) found_index = w;
		});

		if(found_index >= 0) {
			words_in_game.current[found_index].found = true;
		}
		
		if((!is_builder || is_test) && props.doHit != undefined) props.doHit(checkSolved());
		
		if(found_index < 0) {
			hoverStop('error');
			return;
		}

		//TOMA PREMIO

		drawWordLine(words_in_game.current[found_index]);

		hoverStop('success');

		score.current += 100;
		props.onScore(score.current);

		if(props.onFound != undefined) props.onFound(words_in_game.current[found_index].word);

		isSolved();
	};

	const paintFoundLetters = (word) => {
		const x1 = word.start[0];
		const y1 = word.start[1];

		const letter_coords = [];
		switch(word.orientation) {
			case 'vertical':
				for(let i = 0; i < word.word.length; i++) {
					letter_coords.push(`${x1}_${y1 + i}_l`);
				}
				break;

			case 'verticalBack':
				for(let i = 0; i < word.word.length; i++) {
					letter_coords.push(`${x1}_${y1 - i}_l`);
				}
				break;

			case 'horizontal':
				for(let i = 0; i < word.word.length; i++) {
					letter_coords.push(`${x1 + i}_${y1}_l`);
				}
				break;

			case 'horizontalBack':
				for(let i = 0; i < word.word.length; i++) {
					letter_coords.push(`${x1 - i}_${y1}_l`);
				}
				break;

			case 'diagonal':
				for(let i = 0; i < word.word.length; i++) {
					letter_coords.push(`${x1 + i}_${y1 + i}_l`);
				}
				break;

			case 'diagonalBack':
				for(let i = 0; i < word.word.length; i++) {
					letter_coords.push(`${x1 - i}_${y1 + i}_l`);
				}
				break;

			case 'diagonalUp':
				for(let i = 0; i < word.word.length; i++) {
					letter_coords.push(`${x1 + i}_${y1 - i}_l`);
				}
				break;

			case 'diagonalUpBack':
				for(let i = 0; i < word.word.length; i++) {
					letter_coords.push(`${x1 - i}_${y1 - i}_l`);
				}
				break;
		}

		letter_coords.map((letter) => {
			refs.current[letter].setAttrs({
				fill:text_color_ok,
			});
		});
	};

	const drawWordLine = (word, is_missed = false, emulate_found = false) => {
		const is_diagonal = (['diagonal', 'diagonalUp', 'diagonalUpBack', 'diagonalBack'].includes(word.orientation)) ? true : false;
		const is_normal_direction_h = (['horizontal', 'diagonal', 'diagonalUp'].includes(word.orientation)) ? true : false;
		const is_normal_direction_v = (['vertical', 'diagonal'].includes(word.orientation)) ? true : false;

		const x1 = (is_normal_direction_h) ? word.start[0] : word.end[0];
		const y1 = (is_normal_direction_v) ? word.start[1] : word.end[1];
		const x2 = (is_normal_direction_h) ? word.end[0] : word.start[0];
		const y2 = (is_normal_direction_v) ? word.end[1] : word.start[1];

		const line_h = box_size.current * 0.8;
		let line_w = box_size.current * word.word.length;
		if(is_diagonal) {
			// If the word is in any diagonal distribution, the width of the box needs
			// to be larger by a square root of 2 (the unit square diagonal length).
			// We also reduce the length a bit so it fits better the letters without
			// going too far from them at the edges.
			line_w = line_w * Math.sqrt(2) - box_size.current / 2;
		}

		const default_rect_props = {
			width:line_w,
			height:line_h,
			x:x1 * box_size.current + line_w / 2,
			y:y1 * box_size.current + line_h / 2 + box_size.current * 0.12,
			offsetX:line_w / 2,
			offsetY:line_h / 2,
			cornerRadius:line_h / 2,
			opacity:(word.found == undefined && emulate_found === false) ? 0.3 : 1,
		};

		if(['vertical', 'verticalUp'].includes(word.orientation)) {
			default_rect_props.rotation = 90;
			default_rect_props.x = x1 * box_size.current + line_h / 2 + box_size.current * 0.1;
			default_rect_props.y = y1 * box_size.current + line_w / 2;
		}

		if(is_diagonal) {
			default_rect_props.x = (x1 + (x2 + 1 - x1) / 2) * box_size.current;
			default_rect_props.y = (y1 + (y2 + 1 - y1) / 2) * box_size.current;
		}

		if(['diagonal', 'diagonalUpBack'].includes(word.orientation)) {
			default_rect_props.rotation = 45;
		}

		if(['diagonalUp', 'diagonalBack'].includes(word.orientation)) {
			default_rect_props.rotation = -45;
		}

		if(found_stroke_width > 0 && !is_missed) {
			default_rect_props.strokeWidth = parseInt(found_stroke_width);
			default_rect_props.stroke = found_stroke_color;
		}

		const line_group = new Konva.Group({
			opacity:(is_missed) ? 0 : 1,
		});

		const line = new Konva.Rect({
			...default_rect_props,
			fill:(!is_missed) ? color_ok : color_ko,
		});
		refs.current[`line_group_${word.word}_line`] = line;

		if(!is_missed) {
			paintFoundLetters(word);
		}

		line_group.add(line);
		refs.current[`line_group_${word.word}`] = line_group;

		refs.current['letter_lines'].add(line_group);

		if(is_missed) {
			refs.current[`line_group_${word.word}`].to({
				opacity:1,
				duration:0.7,
				easing:Konva.Easings.StrongEaseInOut,
			});
		}
	};

	const highlightMissedWords = () => {
		const missed_words = words_in_game.current.filter((w) => ((w?.found ?? false) === false));

		missed_words.map((missed_word, m) => {
			setTimeout(() => {
				drawWordLine(missed_word, true);
			}, (m * 50));
		});
	};

	const applyLettersCache = () => {
		return;

		puzzle.current.map((row, y) => {
			row.map((letter, x) => {
				if(refs.current[`${x}_${y}_l`] == undefined) return;

				refs.current[`${x}_${y}_l`].cache(default_cache_props);
			});
		});
	};

	const clearLettersCache = () => {
		return;
		
		puzzle.current.map((row, y) => {
			row.map((letter, x) => {
				if(refs.current[`${x}_${y}_l`] == undefined) return;

				refs.current[`${x}_${y}_l`].clearCache();
			});
		});
	};

	const updateGameColors = () => {
		if(refs.current['test_selecting'] == undefined) return;

		// selection test line:
		refs.current['test_selecting'].setAttrs({
			stroke:selection_color,
		});

		// letters:
		puzzle.current.map((row, y) => {
			row.map((letter, x) => {
				refs.current[`${x}_${y}_l`].setAttrs({
					fill:text_color,
				});
			});
		});

		// found letters:
		words_in_game.current.map((word, w) => {
			if(w == 0) return;

			paintFoundLetters(word);

			const found_props = {
				fill:color_ok,
				strokeWidth:parseInt(found_stroke_width),
			};
			if(found_stroke_width > 0) {
				found_props.stroke = found_stroke_color;
			}
			refs.current[`line_group_${word.word}_line`].setAttrs(found_props);
		});

		refs.current['layer'].draw();
	};

	const prepareToMount = () => {
		if(refs.current['letters'] == undefined) return;

		refs.current['letters'].setAttrs({
			x:size.current + 10,
		});

		is_playable.current = false;
		is_mounting.current = false;

		let count_word = 0;
		refs.current['letters'].destroyChildren();
		refs.current['letter_lines'].destroyChildren();

		puzzle.current.map((row, y) => {
			row.map((letter, x) => {
				const is_ok = letter?.is_ok ?? false;

				const init_x = box_size.current * Math.floor((count_word) % columns.current);
				const init_y = box_size.current * Math.floor((count_word) / columns.current);

				count_word += 1;

				const _letter = new Konva.Text({
					id:`${x}_${y}_l`,
					text:letter?.letter?.toUpperCase() ?? "",
					scaleX:0,
					scaleY:0,
					rotation:180,
					x:init_x + box_size.current / 2,
					y:init_y + box_size.current * 0.05 + box_size.current / 2,
					width:box_size.current,
					height:box_size.current,
					offsetX:box_size.current / 2,
					offsetY:box_size.current / 2,
					fontSize:box_size.current * 0.6,
					is_letter_ok:is_ok,
					fontFamily:'Helvetica',
					wrap:'char',
					align:'center',
					verticalAlign:'middle',
					fill:text_color,
				});

				refs.current[`${x}_${y}_l`] = _letter;

				refs.current['letters'].add(_letter);
			});
		});
	};

	const addTestWord = (index) => {
		const test_word = words_in_game.current[index];

		const pointer_ini_x = (test_word.start[0] !== false) ? test_word.start[0] * box_size.current + box_size.current / 2 : -size.current;
		const pointer_ini_y = (test_word.start[1] !== false) ? test_word.start[1] * box_size.current + box_size.current / 2 : -size.current;
	
		const pointer_x = (test_word.end[0] !== false) ? test_word.end[0] * box_size.current + box_size.current / 2 : -size.current;
		const pointer_y = (test_word.end[1] !== false) ? test_word.end[1] * box_size.current + box_size.current / 2 : -size.current;

		const line_test = new Konva.Line({
			points:[pointer_ini_x, pointer_ini_y, pointer_x, pointer_y],
			stroke:selection_color,
			lineCap:'round',
			strokeWidth:box_size.current * 0.6,
		});

		refs.current['test_selecting'] = line_test;

		refs.current['letter_lines'].add(line_test);
	};

	const autoMountBoardAnimation = () => {
		is_mounting.current = true;

		applyLettersCache();

		refs.current['letters'].setAttrs({
			x:0,
		});

		puzzle.current.map((row, y) => {
			row.map((letter, x) => {
				if(refs.current[`${x}_${y}_l`] == undefined) return;

				refs.current[`${x}_${y}_l`].setAttrs({
					rotation:0,
					scaleX:1,
					scaleY:1,
				});
			});
		});

		is_playable.current = true;

		addTestWord(0);
		words_in_game.current.map((word, w) => {
			if(w == 0) return;
			drawWordLine(words_in_game.current[w], false, true);
		});

		refs.current['layer'].draw();
	};

	const mountBoardAnimation = () => {
		if(refs.current['letters'] == undefined) return;
		if(is_mounting.current === true) return;

		if(is_builder && !is_test) {
			autoMountBoardAnimation();
			return;
		}

		is_mounting.current = true;

		applyLettersCache();

		refs.current['letters'].setAttrs({
			x:0,
		});

		let actual_letter = 0;
		let finished = 0;
		puzzle.current.map((row, y) => {
			row.map((letter, x) => {
				actual_letter++;

				setTimeout(() => {
					if(refs.current[`${x}_${y}_l`] == undefined) return;

					refs.current[`${x}_${y}_l`].to({
						rotation:0,
						scaleX:1,
						scaleY:1,
						duration:0.8,
						easing:Konva.Easings.StrongEaseInOut,
						onFinish:() => {
							finished++;
							if(finished == Math.pow(puzzle.current.length, 2)) {
								is_playable.current = true;

								if(refs.current['whole_tapper'] != undefined) {
									refs.current['whole_tapper'].setAttrs({
										y:size.current + 10,
									});
								}

								props.startTimer();
							}
						},
					});
				}, actual_letter * 10);
			});
		});
	};

	const unmountBoardAnimation = (is_solved = true, cb) => {
		let actual_letter = 0;
		let finished = 0;
		let rotation_actual_letter = 0;

		refs.current['whole_tapper'].setAttrs({
			y:0,
		});

		if(is_solved === false) {
			highlightMissedWords();
		} else {
			setTimeout(() => {
				puzzle.current.map((row, y) => {
					row.map((letter, x) => {
						rotation_actual_letter++;

						setTimeout(() => {
							if(refs.current[`${x}_${y}_l`] == undefined) return;

							const is_letter_ok = refs.current[`${x}_${y}_l`].attrs?.is_letter_ok ?? false;
							if(!is_letter_ok) return;

							refs.current[`${x}_${y}_l`].to({
								rotation:360,
								duration:1,
								easing:Konva.Easings.StrongEaseInOut,
							});
						}, (rotation_actual_letter * 10));
					});
				});
			}, 1000);
		}

		puzzle.current.map((row, y) => {
			row.map((letter, x) => {
				actual_letter++;

				setTimeout(() => {
					if(refs.current[`${x}_${y}_l`] == undefined) return;

					const is_letter_ok = refs.current[`${x}_${y}_l`].attrs?.is_letter_ok ?? false;

					refs.current[`${x}_${y}_l`].to({
						scaleX:(is_letter_ok) ? 1 : 0.5,
						scaleY:(is_letter_ok) ? 1 : 0.5,
						duration:0.5,
						easing:Konva.Easings.StrongEaseInOut,
						onFinish:() => {
							finished++;

							if(finished == Math.pow(puzzle.current.length, 2)) {
								if(props.onResetFounds != undefined) props.onResetFounds();
								cb();
							}
						},
					});
				}, (actual_letter * 10));
			});
		});
	};

	const pointer_ini_x = (start_line.current.x !== false) ? start_line.current.x * box_size.current + box_size.current / 2 : -size.current;
	const pointer_ini_y = (start_line.current.y !== false) ? start_line.current.y * box_size.current + box_size.current / 2 : -size.current;

	const pointer_x = (end_line.current.x !== false) ? end_line.current.x * box_size.current + box_size.current / 2 : -size.current;
	const pointer_y = (end_line.current.y !== false) ? end_line.current.y * box_size.current + box_size.current / 2 : -size.current;

	if(full_loaded === false) return null;

	let count_bg = 0;

	const targets_props = {
		fill:'rgba(0, 0, 0, 0)',
		onMouseDown:handleMouseDown,
		onTouchStart:handleMouseDown,
		onMouseOver:handleMouseOver,
		onTouchMove:handleMouseOver,
		onMouseUp:handleMouseUp,
		onTouchEnd:handleMouseUp,
	};

	const main_classes = ['canvas_cont'];
	if(display_outter_border === false) main_classes.push('not_bordered');

	return (
		<Stage
			ref={(node) => { refs.current['stage'] = node; }}
			class={main_classes.join(" ")}
			width={size.current}
			height={size.current}
		>
			<Layer
				ref={(node) => { refs.current['layer'] = node; }}
			>
				<Rect
					x={0}
					y={0}
					fill={bg_color}
					width={size.current}
					height={size.current}
				/>

				<Group ref={(node) => { refs.current['letter_lines'] = node; }} />

				{(end_line.current.x !== false) && (
					<Line
						points={[pointer_ini_x, pointer_ini_y, pointer_x, pointer_y]}
						stroke={selection_color}
						lineCap='round'
						strokeWidth={box_size.current * 0.6}
					/>
				)}

				<Group
					ref={(node) => { refs.current['letters'] = node; }}
					x={size.current + 10}
				/>

				{(!is_builder || is_test) && puzzle.current.map((row, y) => {
					return (
						row.map((letter, x) => {
							const init_x = box_size.current * Math.floor((count_bg) % columns.current);
							const init_y = box_size.current * Math.floor((count_bg) / columns.current);
							count_bg += 1;

							if(move_direction.current === false) {
								return (
									<Circle
										key={`${x}_${y}_bg`}
										{...targets_props}
										radius={box_size.current / 2}
										x={init_x + box_size.current / 2}
										y={init_y + box_size.current / 2}
										pos_x={x}
										pos_y={y}
									/>
								)
							}

							return (
								<Rect
									key={`${x}_${y}_bg`}
									{...targets_props}
									x={init_x + box_size.current / 2}
									y={init_y + box_size.current / 2}
									width={box_size.current}
									height={box_size.current}
									offsetX={box_size.current / 2}
									offsetY={box_size.current / 2}
									pos_x={x}
									pos_y={y}
								/>
							)
						})
					)
				})}

				<Group
					ref={(node) => { refs.current['whole_tapper'] = node; }}
					y={0}
				>
					<Rect
						width={size.current}
						height={size.current}
						fill='white'
						opacity={0}
					/>
				</Group>
			</Layer>
		</Stage>
	)
});

export default WordSearchCanvas;